import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { ConfirmDialog } from "molecules/dialog/ConfirmDialog";
import { LoadingState } from "molecules/loadingState/LoadingState";
import { MissingDataState } from "molecules/missingDataState/MissingDataState";
import { useTranslation } from "react-i18next";
import type { User } from "../../common/service/types/UserTypes.ts";
import { makeStringSort, useSort } from "../../common/utils/hooks/useSort";
import { DataTable } from "molecules/data-table/DataTable";
import { SortHeaderButton } from "molecules/sortAndFilterHeader/SortHeaderButton";
import { Button } from "@exopengithub/exokit/v2";
import { IconTrash } from "@tabler/icons-react";
import { UserAccessCell, UserGroupsCell, UserNameCell } from "./userCells";

import {
	useDeleteUserMutation,
	useGetCompanyUsersQuery,
} from "../../common/redux/api/exopenApi";
import { useState } from "react";

import { InviteUserModal } from "./InviteUserModal";

import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.ts";

export const UserTable = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const { data: users, isLoading: isFetchingUsers } = useGetCompanyUsersQuery(
		companyDomain.id,
	);
	const { items: sortedUsers, sortColumnProps } = useSort({
		keys: ["byName", "byEmail"],
		defaultKey: "byName",
		defaultOrder: "asc",
		rows: users ?? [],
		columns: {
			byName: {
				sort: makeStringSort("name"),
			},
			byEmail: {
				sort: makeStringSort("email"),
			},
		},
	});

	const userByEmail: Record<string, User> = {};
	for (const user of users ?? []) {
		userByEmail[user.email] = user;
	}

	const [userToDelete, setUserToDelete] = useState<User>();
	const [userToEdit, setUserToEdit] = useState<string | undefined>();

	const [deleteUser, { isLoading: isDeletingUser }] = useDeleteUserMutation();

	const onDeleteUser = async () => {
		const result = await deleteUser({
			email: userToDelete!.email,
			companyId: companyDomain.id,
		});
		if ("error" in result) {
			showErrorNotification({
				message: "Det gick inte att ta bort användaren.",
			});
		} else {
			showSuccessNotification({ message: "Användaren är nu borttagen" });
			setUserToDelete(undefined);
		}
	};

	if (isFetchingUsers) {
		return <LoadingState />;
	}

	if (!users) {
		return <MissingDataState />;
	}

	return (
		<>
			<DataTable
				label={t("Users")}
				rows={sortedUsers}
				getRowKey={(user) => user.email}
				columns={[
					{
						id: "name",
						width: "auto",
						header: (
							<SortHeaderButton
								label={t("Name")}
								activeDirection={
									sortColumnProps.byName.selected
										? sortColumnProps.byName.order
										: null
								}
								onToggle={sortColumnProps.byName.onClick}
							/>
						),
						render: (user) => (
							<UserNameCell
								user={user}
								onEdit={(user) => setUserToEdit(user.email)}
							/>
						),
					},
					{
						id: "access",
						width: "auto",
						header: t("Access"),
						render: (user) => (
							<UserAccessCell user={user} companyId={companyDomain.id} />
						),
					},
					{
						id: "email",
						width: "auto",
						header: (
							<SortHeaderButton
								label={t("Email")}
								activeDirection={
									sortColumnProps.byEmail.selected
										? sortColumnProps.byEmail.order
										: null
								}
								onToggle={sortColumnProps.byEmail.onClick}
							/>
						),
						render: (user) => user.email,
					},
					{
						id: "groups",
						width: "auto",
						header: t("Assigned groups"),
						render: (user) => (
							<UserGroupsCell user={user} companyId={companyDomain.id} />
						),
					},
					{
						id: "actions",
						width: "min-content",
						render: (user) => (
							<Button
								aria-label={t("Remove user")}
								onClick={(e) => {
									e.stopPropagation();
									setUserToDelete(user);
								}}
								variant="ghost"
								icon={<IconTrash />}
							/>
						),
					},
				]}
			/>
			<InviteUserModal
				modalOpen={userToEdit !== undefined}
				handleClose={() => {
					setUserToEdit(undefined);
				}}
				user={userToEdit ? userByEmail[userToEdit] : undefined}
				companyId={companyDomain.id}
			/>
			<ConfirmDialog
				destructive
				open={userToDelete !== undefined}
				onClose={() => {
					setUserToDelete(undefined);
				}}
				title={t("Remove user")}
				description={t("Do you want to delete the user?")}
				onConfirm={onDeleteUser}
				confirmButtonLabel={t("Remove user")}
				isLoading={isDeletingUser}
			/>
		</>
	);
};
